// MappingEngine - Core Types
// These types are specifically for the new MappingEngine architecture
// They are separate from legacy types for clean separation

// Sample data types
export interface SampleRow {
  [columnName: string]: string | number | null;
}

export interface SampleValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  sampleValidated: boolean;
  totalRowsValidated: number;
  validatedColumns: string[];
}

// Validation types
export interface ValidationError {
  type:
    | 'type_mismatch'
    | 'required_field'
    | 'invalid_format'
    | 'invalid_value'
    | 'unmapped_required'
    | 'duplicate_mapping'
    | 'missing_required'
    | 'invalid_field';
  message: string;
  columnName?: string;
  row?: number;
  fieldName?: string;
}

export interface ValidationWarning {
  type: 'low_confidence' | 'type_conversion' | 'data_truncation';
  message: string;
  columnName?: string;
  severity: 'low' | 'medium' | 'high';
}

// Core mapping types
export interface FileMapping {
  // Identification
  fileId: string;
  fileName: string;
  formId: number;
  formName: string;
  formType: 'main' | 'subform';

  // Mapping data
  matchType: 'exact' | 'fuzzy' | 'manual';
  columnMappings: Record<string, ColumnMapping>;

  // State
  validation: MappingValidation;
  lastModified: Date;
  createdAt: Date;
}

export interface ColumnMapping {
  // Identification
  columnName: string;

  // Mapping (strict 1:1)
  mapping: FormFieldMapping | MetadataMapping | IgnoredMapping;

  // Metadata
  confidence: number; // 0.0 - 1.0
  autoMatched: boolean;
  status: 'unmapped' | 'auto_mapped' | 'manually_mapped' | 'validated';

  // Validation
  validationErrors: string[];
  lastModified: Date;
}

// Union types for mapping (discriminated unions for type safety)
export type FormFieldMapping = {
  type: 'formField';
  targetField: FormFieldTarget;
};

export type MetadataMapping = {
  type: 'metadata';
  metadataType: MetadataTypeValue;
};

export type IgnoredMapping = {
  type: 'ignored';
};

// Form field target with validation info
export interface FormFieldTarget {
  dataPoint: string;
  fieldType: string;
  required: boolean;
  label: string;
  allowedValues?: string[];
  format?: string;
}

// Metadata types enum
export type MetadataTypeValue =
  | 'subjectData'
  | 'externalSubjectId'
  | 'subjectNumber'
  | 'site'
  | 'siteNumber'
  | 'siteName'
  | 'formId'
  | 'mainFormId'
  | 'deletedForm'
  | 'formIndex'
  | 'trash';

// Mapping validation result
export interface MappingValidation {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  requiredMetadataMapped: boolean;
  hasFormFieldMappings: boolean;
  totalColumnsMapped: number;
  unmappedColumns: string[];
}

// Smart auto-mapping result
export interface SmartAutoMappingResult {
  fileMapping: FileMapping;
  formDetected: boolean;
  formConfidence: number;
  columnsAutoMapped: boolean;
  totalColumnsMapped: number;
  message: string;
  processingTime: number;
}

// API transformation types
export interface ApiMappingFormat {
  file_to_form_mappings: Array<{
    csv_file_id: string;
    form_id: string;
    match_type: string;
  }>;
  column_mappings: Array<{
    csv_file_id: string;
    column_name: string;
    mapping_type: string;
    form_id: string;
    metadata_type?: string;
    target_field?: string;
  }>;
  subform_relationships?: Array<{
    subform_id: string;
    parent_form_id: string;
    is_valid: boolean;
  }>;
}

// Bulk metadata mapping types
export interface BulkMetadataMappingRequest {
  columnName: string;
  metadataType: MetadataTypeValue;
  affectedFiles: Array<{
    fileId: string;
    fileName: string;
    currentMapping?: ColumnMapping;
  }>;
}

export interface BulkMetadataMappingResult {
  appliedToCurrent: boolean;
  appliedToAll: boolean;
  filesUpdated: string[];
  mappingsOverridden: number;
  message: string;
}

// Form detection types
export interface FormDetectionResult {
  form: StudyForm | null;
  confidence: number;
  matchReason: string;
  alternatives?: Array<{
    form: StudyForm;
    confidence: number;
    reason: string;
  }>;
}

// Type guards for discriminated unions
export function isFormFieldMapping(
  mapping: ColumnMapping['mapping']
): mapping is FormFieldMapping {
  return mapping.type === 'formField';
}

export function isMetadataMapping(
  mapping: ColumnMapping['mapping']
): mapping is MetadataMapping {
  return mapping.type === 'metadata';
}

export function isIgnoredMapping(
  mapping: ColumnMapping['mapping']
): mapping is IgnoredMapping {
  return mapping.type === 'ignored';
}

// Import and re-export needed types from parent types (for compatibility)
import type {
  StudyForm,
  FormField,
  SelectedFile,
  MetadataType,
} from '../components/types';

export type { StudyForm, FormField, SelectedFile, MetadataType };
